import React from "react";
import { Typography, TypographyProps, SxProps, Theme } from "@mui/material";
import { BaseAtomProps } from "../../types";
import {
  createMemoizedComponent,
  useRenderPerformance,
  logBundleInfo,
} from "../../utils/performance";

export interface TextProps
  extends Omit<TypographyProps, "variant">,
    BaseAtomProps {
  /**
   * Typography variant with mobile-first considerations
   */
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "caption"
    | "overline"
    | "button";

  /**
   * Text color
   */
  color?:
    | "inherit"
    | "primary"
    | "secondary"
    | "textPrimary"
    | "textSecondary"
    | "error"
    | "warning"
    | "info"
    | "success";

  /**
   * Text alignment
   */
  align?: "inherit" | "left" | "center" | "right" | "justify";

  /**
   * Display as different HTML element
   */
  component?: React.ElementType;

  /**
   * Bold text
   */
  bold?: boolean;

  /**
   * Italic text
   */
  italic?: boolean;

  /**
   * Underlined text
   */
  underline?: boolean;

  /**
   * Uppercase text
   */
  uppercase?: boolean;

  /**
   * Truncate text with ellipsis
   */
  truncate?: boolean;

  /**
   * Number of lines before truncation (works with truncate)
   */
  lines?: number;
}

const TextComponent: React.FC<TextProps> = ({
  variant = "body1",
  color = "inherit",
  align = "inherit",
  component,
  bold = false,
  italic = false,
  underline = false,
  uppercase = false,
  truncate = false,
  lines = 1,
  className,
  children,
  sx,
  ...props
}) => {
  // Performance monitoring
  useRenderPerformance("Text");

  // Log bundle info in development
  React.useEffect(() => {
    logBundleInfo("Text");
  }, []);

  const textSx: SxProps<Theme> = {
    // Style modifiers
    ...(bold && { fontWeight: "bold" }),
    ...(italic && { fontStyle: "italic" }),
    ...(underline && { textDecoration: "underline" }),
    ...(uppercase && { textTransform: "uppercase" }),

    // Truncation styles
    ...(truncate && {
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: lines,
      WebkitBoxOrient: "vertical",
      lineHeight: 1.4,
    }),

    // Responsive line height for better mobile readability
    lineHeight: {
      xs: 1.5, // Mobile - more generous spacing
      md: 1.4, // Desktop - standard spacing
    },

    // Mobile-first responsive font sizing for specific variants
    ...(variant === "h4" && {
      fontSize: {
        xs: "1.125rem", // Mobile
        md: "1.25rem", // Desktop
      },
    }),
    ...(variant === "h6" && {
      fontSize: {
        xs: "0.875rem", // Mobile
        md: "1rem", // Desktop
      },
    }),

    // Merge with provided sx
    ...sx,
  };

  return (
    <Typography
      variant={variant}
      color={color}
      align={align}
      component={component}
      className={className}
      sx={textSx}
      {...props}
    >
      {children}
    </Typography>
  );
};

// Apply memoization with performance monitoring
export const Text = createMemoizedComponent(TextComponent);

// Set display name for debugging
(Text as any).displayName = "Text";
